/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import {
  Fragment,
  MouseEvent as ReactMouseEvent,
  CSSProperties,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useStyles } from '~src/hooks/useStyles';
import { DbAnswer, DbQuestion } from '~src/types/db';
import { styleFn } from './style';
import {
  Avatar,
  Button,
  Card,
  Comment,
  message,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { UserOutlined } from '@ant-design/icons';
import QuestionQuickForm, { AnswerFormValues } from './AnswerQuickForm';
import IconButton from '~components/_common/IconButton/IconButton';
import { useIntl } from 'react-intl';
import { CreateAnswerModal } from '~components/molecules/Tabs/TenderTabs/components/Questions/CreateTenderAnswerModal';
import { ReviseQuestionModal } from '~components/molecules/Tabs/TenderTabs/components/Questions/ReviseQuestionsModal';
import AnswerItem from './AnswerItem';
import { RejectQuestionModal } from '~components/molecules/Tabs/TenderTabs/components/Questions/RejectQuestionModal';
import { DB_STATE } from '~src/types/enums';
import { formatDate } from '~src/utils/formatDate';
import { QuestionsChangeLogModal } from './QuestionsChangeLog/QuestionsChangeLogModal';
import { useQuestionsChangeLog } from '~src/hooks/api-hooks/useQuestionsChangeLog';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import dataContextMenu, {
  DataContextMenuItemType,
} from '~src/data/dataContextMenu';
import { ResourceState } from '~components/atoms/FileTables/TenderShares/TenderSharesListTable';
import { BaseRightClickOverlay } from '~components/atoms/RightClickOverlay/BaseRightClickOverlay';
import { AcceptQuestionModal } from '~components/molecules/Tabs/TenderTabs/components/Questions/AcceptQuestionModal';
import { appLog } from '~src/utils/log';
import { queryCache, useMutation } from 'react-query';
import { manageCurrentCorrectionLetterQuestions } from '~src/services/api/movements';
import { QueryKeys } from '~src/hooks/api-hooks/queryKeys';

export type QuestionItemProps = {
  question: DbQuestion;
  questionItemConfig?: {
    withAnswerAuthor?: boolean;
    withQuestionAuthor?: boolean;
    withStatus?: boolean;
    changeLogData?: {
      tenderId: string;
      projectId: number;
    };
  };
  onAnswer?: (answer: AnswerFormValues) => Promise<unknown>;
  onAccept?: () => Promise<unknown>;
  onReject?: () => Promise<unknown>;
  onIgnore?: () => Promise<unknown>;
  onReactivate?: () => Promise<unknown>;
  onMenuDetailsClick: (
    e: ReactMouseEvent<HTMLAnchorElement, MouseEvent>,
    record: DbQuestion
  ) => void;
  isOnTender?: boolean;
};

const QuestionItem: React.FC<QuestionItemProps> = ({
  question,
  questionItemConfig,
  onAnswer,
  onAccept,
  onReject,
  onIgnore,
  onReactivate,
  isOnTender,
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);

  const actionsAllowed = !!onAnswer;

  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [
    isChangeLogModalVisible,
    setIsChangeLogModalVisible,
  ] = useState<DbQuestion | null>(null);
  const [
    isExtendedAnswerFormVisible,
    setIsExtendedAnswerFormVisible,
  ] = useState<DbQuestion | null>(null);
  const [
    isReviseFromVisible,
    setIsReviseFromVisible,
  ] = useState<DbQuestion | null>(null);
  const [
    isRejectModalVisible,
    setIsRejectModalVisible,
  ] = useState<DbQuestion | null>(null);
  const [
    isAcceptedModalVisible,
    setIsAcceptedModalVisible,
  ] = useState<DbQuestion | null>(null);
  const [showAnswerForm, setShowAnswerForm] = useState<DbQuestion | null>(null);

  const changesLogQuery = useQuestionsChangeLog(
    {
      questionId: question.id,
      tenderId: questionItemConfig?.changeLogData?.tenderId || '',
      projectId: questionItemConfig?.changeLogData?.projectId,
    },
    { enabled: false }
  );

  useEffect(() => {
    if (isChangeLogModalVisible) changesLogQuery.refetch();
  }, [isChangeLogModalVisible]);

  const handleSendAnswer = async (answerData: AnswerFormValues) => {
    await onAnswer?.(answerData);
    setShowAnswerForm(false);
  };

  const questionsStateTag = useMemo(() => {
    if (question.state === DB_STATE.ACCEPTED)
      return {
        label: intl.formatMessage({ id: 'accepted' }).toUpperCase(),
        color: '#4AC6AE',
        icon: <FontAwesomeIcon icon={['far', 'circle-check']} />,
      };
    if (question.state === DB_STATE.REJECTED)
      return {
        label: intl.formatMessage({ id: 'rejected' }).toUpperCase(),
        color: '#F65D64',
        icon: <FontAwesomeIcon icon={['far', 'ban']} />,
      };

    if (questionItemConfig?.withStatus) {
      return {
        label: intl.formatMessage({ id: 'pending' }).toUpperCase(),
        color: '#C0C0C0',
        icon: '...',
      };
    }

    return null;
  }, [question.state, questionItemConfig?.withStatus]);

  const [selectedItems, setSelectedItems] = useState<DbQuestion[]>([]);
  const [rightClickEntity, setRightClickEntity] = useState<{
    question?: DbQuestion;
  }>({});
  const [rightClickAnswer, setRightClickAnswer] = useState<{
    answer?: DbAnswer;
  }>({});

  const rightClickSelect = (e: ReactMouseEvent<any>, question: DbQuestion) => {
    e.preventDefault();
    setRightClickEntity({ question });
  };

  const projectId = questionItemConfig?.changeLogData?.projectId;
  const tenderId = questionItemConfig?.changeLogData?.tenderId;

  const [doManageQuestionsToLetter] = useMutation(
    manageCurrentCorrectionLetterQuestions,
    {
      onSuccess: () => {
        if (projectId && tenderId) {
          queryCache.invalidateQueries([
            QueryKeys.question,
            { projectId, tenderId },
          ]);
        }

        message.success(
          intl.formatMessage({ id: 'correction-letter-questions-updated' })
        );
      },
      onError: (error, payload) => {
        message.error(
          intl.formatMessage({
            id: 'correction-letter-questions-update-failed',
          })
        );
        appLog('doManageQuestionsToLetter -> payload', payload);
        appLog('doManageQuestionsToLetter -> error', error);
      },
    }
  );

  const handleContextMenuEvent = (e: ReactMouseEvent<any>) => {
    if (isOnTender) {
      e.preventDefault();
      return;
    }

    e.preventDefault();
    const customEvent = new MouseEvent('contextmenu', {
      bubbles: true,
      cancelable: true,
      view: window,
      button: 2,
      buttons: 2,
      clientX: e.clientX,
      clientY: e.clientY,
    });
    e.currentTarget.dispatchEvent(customEvent);
  };

  const handleRightOverlayItemClick = (item: DataContextMenuItemType) => {
    switch (item.modal) {
      case 'revise-and-accept':
        setIsReviseFromVisible(rightClickEntity.question || null);
        break;
      case 'messages-history':
        setIsChangeLogModalVisible(rightClickEntity.question || null);
        break;
      case 'accept':
        setIsAcceptedModalVisible(rightClickEntity.question || null);
        break;
      case 'answer':
        setIsExtendedAnswerFormVisible(rightClickEntity.question || null);
        break;
      case 'reject':
        setIsRejectModalVisible(rightClickEntity.question || null);
        break;
      case 'delete':
        appLog('delete');
        break;
      case 'ignore':
        onIgnore?.();
        setRightClickEntity({});
        break;
      case 'reactivate':
        onReactivate?.();
        setRightClickEntity({});
        break;

      case 'add-to-correction-letter': {
        const questionId = rightClickEntity.question?.id;
        if (!questionId) break;

        if (!projectId || !tenderId) {
          appLog('add-to-correction-letter: missing projectId/tenderId', {
            projectId,
            tenderId,
            questionId,
          });
          break;
        }

        doManageQuestionsToLetter({
          projectId,
          tenderId: Number(tenderId),
          action: 'add_question',
          body: {
            question_ids: [questionId],
          },
        });

        setRightClickEntity({});
        break;
      }

      case 'remove-from-correction-letter': {
        const questionId = rightClickEntity.question?.id;
        if (!questionId) break;

        if (!projectId || !tenderId) {
          appLog('remove-from-correction-letter: missing projectId/tenderId', {
            projectId,
            tenderId,
            questionId,
          });
          break;
        }

        doManageQuestionsToLetter({
          projectId,
          tenderId: Number(tenderId),
          action: 'remove_question',
          body: {
            question_ids: [questionId],
          },
        });

        setRightClickEntity({});
        break;
      }

      default:
        break;
    }
  };

  const overlayItems = useMemo(() => {
    if (isOnTender) return [];

    const state = rightClickEntity.question?.state;
    const isPending =
      state !== ResourceState.ACCEPTED && state !== ResourceState.REJECTED;

    const isInLetter = rightClickEntity.question?.report_data
      ? rightClickEntity.question.report_data.is_in_current_report === true
      : false;

    const isReportSent =
      rightClickEntity.question?.report_data?.is_report_sent === true ||
      hasFirstReportDatetime(
        rightClickEntity.question?.report_data?.first_report_datetime
      );

    const withDisabled = dataContextMenu.questionAndAnswer.map((item) => {
      const isAccepted = state === ResourceState.ACCEPTED;
      const isRejected = state === ResourceState.REJECTED;

      // Accept/Revise
      if (isAccepted && ['accept', 'revise-and-accept'].includes(item.name)) {
        return { ...item, disabled: true };
      }

      // Reject
      if (isRejected && item.name === 'reject') {
        return { ...item, disabled: true };
      }

      // disable Reject, if question is accepted and already included in report
      if (isAccepted && isReportSent && item.name === 'reject') {
        return { ...item, disabled: true };
      }

      return { ...item, disabled: false };
    });

    const isIgnored =
      rightClickEntity.question?.report_data?.is_ignore === true;

    return withDisabled.filter((it) => {
      const isAccepted = state === ResourceState.ACCEPTED;

      if (isPending && ['ignore', 'reactivate'].includes(it.modal)) {
        return false;
      }

      // question status - accepted: show correction btns
      if (
        ['add-to-correction-letter', 'remove-from-correction-letter'].includes(
          it.modal
        ) &&
        !isAccepted
      ) {
        return false;
      }

      if (it.modal === 'add-to-correction-letter') return !isInLetter;
      if (it.modal === 'remove-from-correction-letter') return isInLetter;

      if (it.modal === 'ignore') return !isIgnored;
      if (it.modal === 'reactivate') return isIgnored;

      return true;
    });
  }, [
    rightClickEntity.question?.state,
    rightClickEntity.question?.id,
    isOnTender,
    rightClickEntity.question?.report_data?.is_in_current_report,
    rightClickEntity.question?.report_data?.is_ignore,
    rightClickEntity.question?.report_data?.is_report_sent,
    rightClickEntity.question?.report_data?.first_report_datetime,
  ]);

  return (
    <div css={styles.container}>
      <BaseRightClickOverlay
        items={overlayItems}
        disabled={!!selectedItems.length}
        onItemClick={handleRightOverlayItemClick}
        onOverlayClose={() => setRightClickEntity({})}>
        <Card
          bodyStyle={{ padding: '10px 15px', paddingLeft: '25px' }}
          css={styles.cardContainer}
          onContextMenu={(e) => rightClickSelect(e, question)}>
          <Comment
            /* Avatar & Name & Company */
            author={
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                }}>
                {/* hide avatar on vendor side */}
                {!isOnTender && (
                  <Avatar size="large" icon={<UserOutlined rev="user" />} />
                )}
                <div
                  style={{
                    /* Прибираємо відступ, якщо немає аватара */
                    paddingLeft: isOnTender ? '0px' : '15px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                  }}>
                  {questionItemConfig?.withQuestionAuthor ? (
                    <Fragment>
                      <Typography style={{ fontSize: '16px', fontWeight: 600 }}>
                        {question.created_by?.full_name}
                      </Typography>
                      <Typography
                        style={{ color: '#8c8c8c', fontSize: '12px' }}>
                        {question.created_by?.email}
                        {question.created_by?.company_name
                          ? ` | ${question.created_by.company_name}`
                          : ''}
                      </Typography>
                    </Fragment>
                  ) : (
                    <Typography style={{ color: '#8c8c8c', fontSize: '14px' }}>
                      {intl.formatMessage({
                        id: 'qa-question-author-not-found',
                      })}
                    </Typography>
                  )}
                </div>
              </div>
            }
            /* 2. Created At */
            datetime={
              <div
                style={{
                  color: '#8c8c8c',
                  paddingRight: '120px',
                  display: 'flex',
                  alignItems: 'center',
                  whiteSpace: 'nowrap',
                }}>
                <span>{formatDate(question.created_at, 'long')}</span>
                {!!question.change_instances && (
                  <span style={{ marginLeft: '8px' }}>
                    ({intl.formatMessage({ id: 'qa-question-edited' })})
                  </span>
                )}
              </div>
            }
            content={
              <Typography
                css={{
                  paddingTop: '15px',
                  whiteSpace: 'pre-line',
                  wordBreak: 'break-word',
                }}>
                {question.question}
              </Typography>
            }>
            {/* 3.Action container status */}
            {(!isOnTender ||
              (questionItemConfig?.withStatus && questionsStateTag) ||
              actionsAllowed) && (
              <div css={styles.actionsContainer}>
                <QuestionStatusIcons question={question} intl={intl} />
                {!!questionItemConfig?.withStatus && questionsStateTag && (
                  <Tag
                    icon={questionsStateTag.icon}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '2px',
                      color: questionsStateTag.color,
                    }}>
                    <div>{questionsStateTag.label}</div>
                  </Tag>
                )}
                {actionsAllowed && (
                  <div
                    visible={isDropdownVisible}
                    onVisibleChange={(visible) =>
                      setIsDropdownVisible(visible)
                    }>
                    <IconButton
                      click={(e) => {
                        handleContextMenuEvent(e);
                        setIsDropdownVisible((prev) => !prev);
                      }}
                      icon={['far', 'ellipsis']}
                    />
                  </div>
                )}
              </div>
            )}
          </Comment>

          <div onContextMenu={(e) => e.stopPropagation()}>
            {question.answers?.map((answer) => (
              <AnswerItem
                key={answer.id}
                answer={answer}
                canDelete={actionsAllowed}
                withAnswerAuthor={questionItemConfig?.withAnswerAuthor}
                isOnTender={isOnTender}
                projectId={projectId}
                tenderId={tenderId}
              />
            ))}
          </div>

          {actionsAllowed && !showAnswerForm && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: '10px',
              }}>
              <Button
                type="link"
                onClick={() => setShowAnswerForm((prev) => !prev)}
                icon={<FontAwesomeIcon icon={['fas', 'plus']} />}>
                <span style={{ color: 'black', paddingLeft: '5px' }}>
                  {intl.formatMessage({
                    id: 'quick-answer',
                  })}
                </span>
              </Button>
              <Button
                type="link"
                onClick={() => setIsExtendedAnswerFormVisible(question)}
                icon={<FontAwesomeIcon icon={['fas', 'pen']} />}>
                <span style={{ color: 'black', paddingLeft: '5px' }}>
                  {intl.formatMessage({
                    id: 'answer',
                  })}
                </span>
              </Button>
            </div>
          )}
          {showAnswerForm && (
            <QuestionQuickForm
              onAnswer={handleSendAnswer}
              onCancelForm={() => setShowAnswerForm(null)}
            />
          )}
        </Card>
      </BaseRightClickOverlay>

      {questionItemConfig?.changeLogData && (
        <QuestionsChangeLogModal
          visible={isChangeLogModalVisible}
          onCancel={() => setIsChangeLogModalVisible(null)}
          questionId={question.id}
          questionLogs={changesLogQuery.data}
          isLoading={changesLogQuery.isLoading}
        />
      )}
      <CreateAnswerModal
        visible={isExtendedAnswerFormVisible}
        onCancel={() => setIsExtendedAnswerFormVisible(null)}
        question={question}
      />
      <ReviseQuestionModal
        visible={isReviseFromVisible}
        onCancel={() => setIsReviseFromVisible(null)}
        question={question}
      />
      <RejectQuestionModal
        visible={isRejectModalVisible}
        onCancel={() => setIsRejectModalVisible(null)}
        onReject={onReject}
      />
      <AcceptQuestionModal
        visible={isAcceptedModalVisible}
        onCancel={() => setIsAcceptedModalVisible(null)}
        onAccept={onAccept}
      />
    </div>
  );
};

export default QuestionItem;

const hasFirstReportDatetime = (val: any): boolean => {
  if (val === null || val === undefined) return false;
  const num = Number(val);
  return !Number.isNaN(num) && num > 0;
};

const QuestionStatusIcons = ({
  question,
  intl,
}: {
  question: DbQuestion;
  intl: any;
}) => {
  const hasIgnore = question?.report_data?.is_ignore === true;
  const hasReportSent =
    question?.report_data?.is_report_sent === true ||
    hasFirstReportDatetime(question?.report_data?.first_report_datetime);

  const hasInCurrentLetter =
    question?.report_data?.is_in_current_report === true;

  if (!hasIgnore && !hasReportSent && !hasInCurrentLetter) return null;

  const wrap = (node: any, id: string, defaultMessage: string) => (
    <Tooltip title={intl.formatMessage({ id, defaultMessage })}>
      <span style={{ display: 'inline-flex', alignItems: 'center' }}>
        {node}
      </span>
    </Tooltip>
  );

  const iconWrapStyle: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1,
  };

  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 0 }}>
      {hasInCurrentLetter
        ? wrap(
            <span style={iconWrapStyle}>
              <IconButton
                iconStyle="far"
                icon="memo"
                color="blue"
                backgroundOff
              />
            </span>,
            'movement-flag-in-current-letter',
            'Included in current correction letter'
          )
        : null}

      {hasIgnore
        ? wrap(
            <span style={iconWrapStyle}>
              <IconButton
                iconStyle="fas"
                icon="ban"
                color="red"
                backgroundOff
              />
            </span>,
            'movement-flag-ignored',
            'Ignored'
          )
        : null}

      {hasReportSent
        ? wrap(
            <span style={iconWrapStyle}>
              <IconButton
                iconStyle="fas"
                icon="memo-circle-check"
                color="green"
                backgroundOff
              />
            </span>,
            'movement-flag-report-sent',
            'Already included in a sent report'
          )
        : null}
    </div>
  );
};
